import { z } from "zod"

import { hasSignificantFigures } from "./utils"

import { DataType, TestTemplateComparison } from "@/graphql/codegen/graphql"
import { isNumericString } from "@/utils/forms"
import {
  validateNumericField,
  validateRequiredField,
  validateStringMaxLength,
  validateValueInRange,
} from "@/utils/schema"

const LIMIT_VALUE_MAX_LENGTH = 10 // AS400 constraint

const LIMIT_VALUE_MIN = -99999.99999 // AS400 constraint

const LIMIT_VALUE_MAX = 99999.99999 // AS400 constraint

type AssociatedTestTemplateFormSchema = z.ZodObject<{
  equalToValue: z.ZodString
  maximumValue: z.ZodString
  minimumValue: z.ZodString
  dataType: z.ZodUnion<[z.ZodNativeEnum<typeof DataType>, z.ZodLiteral<"">]>
  significantFigures: z.ZodString
  printToAkrochem: z.ZodEffects<z.ZodNullable<z.ZodBoolean>>
}>

const validateEqualToComparison = (data: AssociatedTestTemplateFormValues, ctx: z.RefinementCtx): void => {
  const hasPassedRequired = validateRequiredField(data.equalToValue, "Equal to Value", ctx, ["equalToValue"])

  if (!hasPassedRequired) return

  if (isNumericString(data.equalToValue)) {
    validateValueInRange(data.equalToValue, "Equal to Value", LIMIT_VALUE_MIN, LIMIT_VALUE_MAX, ctx, ["equalToValue"])
  } else {
    validateStringMaxLength(data.equalToValue, "Equal to Value", LIMIT_VALUE_MAX_LENGTH, ctx, ["equalToValue"])
  }
}

const validateMaximumComparison = (data: AssociatedTestTemplateFormValues, ctx: z.RefinementCtx): void => {
  const hasPassedRequired = validateRequiredField(data.maximumValue, "Maximum Value", ctx, ["maximumValue"])

  if (!hasPassedRequired) return

  const isNumericFieldPassed = validateNumericField(data.maximumValue, "Maximum Value", ctx, ["maximumValue"])

  if (!isNumericFieldPassed) return

  validateValueInRange(data.maximumValue, "Maximum Value", LIMIT_VALUE_MIN, LIMIT_VALUE_MAX, ctx, ["maximumValue"])
  validateDecimalPlaces(data.maximumValue, data.significantFigures, "Maximum Value", ctx, ["maximumValue"])
}

const validateMinimumComparison = (data: AssociatedTestTemplateFormValues, ctx: z.RefinementCtx): void => {
  const hasPassedRequired = validateRequiredField(data.minimumValue, "Minimum Value", ctx, ["minimumValue"])

  if (!hasPassedRequired) return

  const isNumericFieldPassed = validateNumericField(data.minimumValue, "Minimum Value", ctx, ["minimumValue"])

  if (!isNumericFieldPassed) return

  validateValueInRange(data.minimumValue, "Minimum Value", LIMIT_VALUE_MIN, LIMIT_VALUE_MAX, ctx, ["minimumValue"])
  validateDecimalPlaces(data.minimumValue, data.significantFigures, "Minimum Value", ctx, ["minimumValue"])
}

const validateRangeComparison = (data: AssociatedTestTemplateFormValues, ctx: z.RefinementCtx): void => {
  const isMinPassedRequired = validateRequiredField(data.minimumValue, "Range Min. Value", ctx, ["minimumValue"])
  const isMaxPassedRequired = validateRequiredField(data.maximumValue, "Range Max. Value", ctx, ["maximumValue"])

  if (data.dataType === DataType.Numeric) {
    // Validate minimum value if provided
    if (data.minimumValue && data.minimumValue.trim() !== "") {
      const isMinNumericValid = validateNumericField(data.minimumValue, "Range Min. Value", ctx, ["minimumValue"])

      if (isMinNumericValid) {
        validateValueInRange(data.minimumValue, "Range Min. Value", LIMIT_VALUE_MIN, LIMIT_VALUE_MAX, ctx, [
          "minimumValue",
        ])
        validateDecimalPlaces(data.minimumValue, data.significantFigures, "Range Min. Value", ctx, ["minimumValue"])
      }
    }

    // Validate maximum value if provided
    if (data.maximumValue && data.maximumValue.trim() !== "") {
      const isMaxNumericValid = validateNumericField(data.maximumValue, "Range Max. Value", ctx, ["maximumValue"])

      if (isMaxNumericValid) {
        validateValueInRange(data.maximumValue, "Range Max. Value", LIMIT_VALUE_MIN, LIMIT_VALUE_MAX, ctx, [
          "maximumValue",
        ])
        validateDecimalPlaces(data.maximumValue, data.significantFigures, "Range Max. Value", ctx, ["maximumValue"])
      }
    }

    // Cross-field validation: only if both fields are provided
    if (isMinPassedRequired && isMaxPassedRequired) {
      const minValue = Number(data.minimumValue)
      const maxValue = Number(data.maximumValue)

      if (!Number.isNaN(minValue) && !Number.isNaN(maxValue) && minValue >= maxValue) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Range Min. Value must be less than Range Max. Value",
          path: ["minimumValue"],
        })
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Range Max. Value must be greater than Range Min. Value",
          path: ["maximumValue"],
        })
      }
    }
  } else if (data.dataType === DataType.Alphanumeric) {
    if (data.minimumValue && data.minimumValue.trim() !== "") {
      validateStringMaxLength(data.minimumValue, "Range Min. Value", LIMIT_VALUE_MAX_LENGTH, ctx, ["minimumValue"])
    }
    if (data.maximumValue && data.maximumValue.trim() !== "") {
      validateStringMaxLength(data.maximumValue, "Range Max. Value", LIMIT_VALUE_MAX_LENGTH, ctx, ["maximumValue"])
    }
  }
}

const validateSignificantFigures = (data: AssociatedTestTemplateFormValues, ctx: z.RefinementCtx): void => {
  if (!data.significantFigures || data.significantFigures.trim() === "") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Significant figures value is required",
      path: ["significantFigures"],
    })
  }
}

const validateDecimalPlaces = (
  value: string,
  significantFigures: string,
  fieldName: string,
  ctx: z.RefinementCtx,
  path: string[]
): boolean => {
  if (!significantFigures || significantFigures.trim() === "" || !isNumericString(value)) {
    return true // Skip validation if significant figures not set or value is not numeric
  }

  const maxDecimalPlaces = Number.parseInt(significantFigures, 10)
  const decimalPart = value.split(".")[1]
  const actualDecimalPlaces = decimalPart ? decimalPart.length : 0

  if (actualDecimalPlaces > maxDecimalPlaces) {
    const message =
      maxDecimalPlaces === 0
        ? `${fieldName} cannot have decimal places when significant figures is 0`
        : `${fieldName} cannot have more than ${maxDecimalPlaces} decimal place${maxDecimalPlaces > 1 ? "s" : ""}`

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path,
    })
    return false
  }

  return true
}

export const createAssociatedTestTemplateFormSchema = (
  comparison?: TestTemplateComparison
): z.ZodEffects<AssociatedTestTemplateFormSchema> =>
  z
    .object({
      equalToValue: z.string(),
      maximumValue: z.string(),
      minimumValue: z.string(),
      dataType: z.union([z.nativeEnum(DataType), z.literal("")]),
      significantFigures: z.string(),
      printToAkrochem: z
        .boolean()
        .nullable()
        .refine((value) => value !== null, { message: "Print to Akrochem is required" }),
    })
    .superRefine((data, ctx) => {
      switch (comparison) {
        case TestTemplateComparison.EqualTo:
          validateEqualToComparison(data, ctx)
          break
        case TestTemplateComparison.Maximum:
          validateMaximumComparison(data, ctx)
          break
        case TestTemplateComparison.Minimum:
          validateMinimumComparison(data, ctx)
          break
        case TestTemplateComparison.Range:
          validateRangeComparison(data, ctx)
          break
      }

      if (comparison && hasSignificantFigures(comparison, data.dataType as DataType)) {
        validateSignificantFigures(data, ctx)
      }
    })

export type AssociatedTestTemplateFormValues = z.infer<ReturnType<typeof createAssociatedTestTemplateFormSchema>>

export const productOperatorActionFormSchema = z.object({
  isOperatorActionRequired: z.boolean().optional(),
  operatorActionDescription: z.string().optional(),
})

export type ProductOperatorActionFormValues = z.infer<typeof productOperatorActionFormSchema>

export const productPreTestNoteFormSchema = z.object({
  printExpirationToAkrochemCoa: z.boolean().optional(),
  printMfgDatesToAkrochemCoa: z.boolean().optional(),
  printPreTestNotesToAkrochemCoa: z.boolean().optional(),
  preTestNotes: z.string().optional(),
})

export type ProductPreTestNoteFormValues = z.infer<typeof productPreTestNoteFormSchema>

export const productPostTestNoteFormSchema = z.object({
  printPostTestNotesToAkrochemCoa: z.boolean().optional(),
  postTestNotes: z.string().optional(),
})

export type ProductPostTestNoteFormValues = z.infer<typeof productPostTestNoteFormSchema>
